import { useState, type FormEvent } from 'react';
import type { Branding, Section } from '../types.ts';
import { PrimaryButton } from './ui.tsx';
import { PRODUCT_NAME } from '../brand.ts';
import { api } from '../api.ts';

export function WelcomeScreen({ branding, onStart }: { branding: Branding; onStart: () => void }) {
  return (
    <div className="text-center">
      <h1 className="text-[34px] font-bold leading-tight text-ink">{PRODUCT_NAME}</h1>
      <p className="mt-2 text-lg font-medium text-ink-soft">Prepared for {branding.clientName}</p>
      {branding.welcomeCopy && (
        <p className="mx-auto mt-4 max-w-[52ch] text-lg text-ink-soft">{branding.welcomeCopy}</p>
      )}
      <ul className="mx-auto mt-8 flex max-w-sm flex-col gap-2 text-ink-soft">
        <li>Estimated completion time: 30 minutes</li>
        <li>Progress saved automatically</li>
      </ul>
      <div className="mt-10">
        <PrimaryButton onClick={onStart}>Start Assessment</PrimaryButton>
      </div>
    </div>
  );
}

export function LoginScreen({
  branding,
  onSuccess,
}: {
  branding: Branding;
  onSuccess: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(branding.slug ?? '', username, password);
      onSuccess();
    } catch {
      setError('Invalid credentials. Please check and try again.');
    } finally {
      setBusy(false);
    }
  };

  const field =
    'w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]';

  return (
    <div className="mx-auto w-full max-w-sm">
      <h1 className="text-2xl font-semibold text-ink">Sign in to continue</h1>
      <p className="mt-1 text-sm text-ink-soft">Enter the survey credentials provided by your administrator.</p>
      <form className="mt-6 flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
          Username
          <input className={field} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
          Password
          <input className={field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign In'}
        </PrimaryButton>
      </form>
      <p className="mt-4 text-sm text-ink-soft">Trouble signing in? Contact your admin.</p>
    </div>
  );
}

export function ReviewScreen({
  sections,
  answers,
  blanks,
  total,
  onEditQuestion,
  onSubmit,
  submitting,
}: {
  sections: Section[];
  answers: Record<string, number | null>;
  blanks: number;
  total: number;
  onEditQuestion: (sectionIdx: number, questionIdx: number) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleSubmit = () => {
    if (blanks > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    onSubmit();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-ink">Review your answers</h1>
      <p className="mt-2 text-ink-soft">Expand any dimension and click a question to jump straight to it.</p>
      <ul className="mt-6 divide-y divide-black/5 overflow-hidden rounded-xl border border-black/5 bg-white">
        {sections.map((s, sIdx) => {
          const answered = s.questions.filter((q) => typeof answers[String(q.externalRef)] === 'number').length;
          const isOpen = open === sIdx;
          return (
            <li key={s.slug}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : sIdx)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-black/2"
              >
                <span className="flex items-center gap-2 text-ink">
                  <span aria-hidden className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>›</span>
                  {s.name}
                </span>
                <span className="text-sm font-medium text-ink-soft">
                  {answered} of {s.questions.length} answered
                </span>
              </button>
              {isOpen && (
                <ul className="border-t border-black/5 bg-black/[0.015]">
                  {s.questions.map((q, qIdx) => {
                    const level = answers[String(q.externalRef)];
                    const anchor =
                      typeof level === 'number' ? q.anchors.find((a) => a.level === level) : undefined;
                    return (
                      <li key={q.externalRef}>
                        <button
                          type="button"
                          onClick={() => onEditQuestion(sIdx, qIdx)}
                          className="flex w-full flex-col gap-1 px-5 py-2.5 text-left hover:bg-black/2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--accent)]"
                        >
                          <span className="text-sm text-ink">{q.prompt}</span>
                          <span className="text-xs">
                            {typeof level === 'number' ? (
                              <span className="font-medium text-ink">
                                {level}
                                {anchor ? ` · ${anchor.label}` : ''}
                              </span>
                            ) : level === null ? (
                              <span className="font-medium text-amber-600">Skipped</span>
                            ) : (
                              <span className="font-medium text-red-600">Not answered</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {confirming && blanks > 0 && (
        <div
          role="alertdialog"
          aria-label="Confirm submission with unanswered questions"
          className="mt-8 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-medium">
            You left {blanks} of {total} question{blanks === 1 ? '' : 's'} unanswered.
          </p>
          <p className="mt-1 text-amber-800">
            Skipped questions are simply left out of the analysis — you can go back and answer them, or
            submit as is.
          </p>
        </div>
      )}
      <div className="mt-8 flex items-center justify-end gap-3">
        {confirming && blanks > 0 && (
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="text-sm font-medium text-ink-soft underline-offset-4 hover:underline"
          >
            Go back
          </button>
        )}
        <PrimaryButton onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? 'Submitting…'
            : confirming && blanks > 0
              ? 'Submit anyway'
              : 'Submit Assessment'}
        </PrimaryButton>
      </div>
    </div>
  );
}

export function ThankYouScreen() {
  return (
    <div className="text-center">
      <h1 className="text-3xl font-semibold text-ink">Thank you for completing the assessment.</h1>
      <p className="mt-3 text-ink-soft">Your responses have been submitted.</p>
      <p className="mt-2 text-ink-soft">
        The Wipro team will analyze the results and follow up with your organization.
      </p>
    </div>
  );
}

export function MessageScreen({ title, body }: { title: string; body: string }) {
  return (
    <div className="text-center">
      <h1 className="text-2xl font-semibold text-ink">{title}</h1>
      <p className="mt-3 text-ink-soft">{body}</p>
    </div>
  );
}
